import { doc, type DocumentReference } from 'firebase/firestore';
import { computed, makeObservable, observable } from 'mobx';

import { getFirestore } from 'services/Firebase';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { type StarredDocument } from 'types/StarredDocument';

import Client from './Client';
import Creative from './Creative';
import DocumentFolder, { type DocumentFolderParent } from './DocumentFolder';
import Project from './Project';
import type User from './User';

function createDocumentFolderForType(
    type: DocumentType,
    parent: DocumentFolderParent,
    docRef: DocumentReference
) {
    switch (type) {
        case DocumentType.CLIENT:
            return new Client(parent, docRef);
        case DocumentType.PROJECT:
            return new Project(parent, docRef);
        case DocumentType.CREATIVE:
            return new Creative(parent, docRef);
        default:
            return new DocumentFolder(parent, docRef);
    }
}

interface DiffRecord {
    folder: DocumentFolder<any, any> | null;
    star: StarredDocument | null;
}

class StarredDocuments {
    private readonly parent: User;
    public documents: DocumentFolder<any, any>[] = [];

    private status: DocumentStatus = DocumentStatus.LOADING;
    // Set by the first completed load and never cleared: the dashboard shows
    // its spinner only until then, and on later visits refreshes behind the
    // list it already has. Logout replaces this whole object (User.onReset).
    private _firstLoadComplete = false;

    // Reconciliations run one at a time, each starting from the documents
    // the previous one settled on. update() is called unawaited on every
    // user-document snapshot, so without this two quick star toggles could
    // overlap and the slower one would overwrite the other's result.
    private queue: Promise<void> = Promise.resolve();

    constructor(parent: User) {
        this.parent = parent;
        makeObservable<StarredDocuments, 'status' | '_firstLoadComplete'>(
            this,
            {
                documents: observable.ref,
                status: observable,
                _firstLoadComplete: observable,
                loading: computed,
            }
        );
    }

    public get loading() {
        return this.status === DocumentStatus.LOADING;
    }

    public get firstLoadComplete() {
        return this._firstLoadComplete;
    }

    public async load() {
        this.status = DocumentStatus.LOADING;
        try {
            // Reload every starred document, not just new ones: the stubs
            // don't self-update, so this is what refreshes them.
            await this.enqueue(this.parent.data.starredDocuments, false);
        } finally {
            this._firstLoadComplete = true;
            this.status = DocumentStatus.LOADED;
        }
    }

    public update(newStarDocRefs: ReadonlyArray<StarredDocument>) {
        return this.enqueue(newStarDocRefs, true);
    }

    private enqueue(
        newStarDocRefs: ReadonlyArray<StarredDocument>,
        keepExisting: boolean
    ) {
        this.queue = this.queue
            .catch(() => {})
            .then(() => this.reconcile(newStarDocRefs, keepExisting));
        return this.queue;
    }

    private async reconcile(
        newStarDocRefs: ReadonlyArray<StarredDocument>,
        keepExisting: boolean
    ) {
        // Each element of this.documents must be compared against
        // newStarDocRefs, keying by their .path fields, and classified as
        // one of:
        // - Removed: A path is present in this.documents but not in
        //   newStarDocRefs.
        // - Added: A path is present in newStarDocRefs but not in
        //   this.documents.
        // - Existing: A path is present in both.

        // Start by creating a new Map, indexed by path, that will contain
        // both folder docs and star docs. We base this off the folder docs
        // and start with an empty slot for a star doc.
        const diff = new Map<string, DiffRecord>(
            this.documents.map((folder) => [
                folder.path,
                { folder, star: null },
            ])
        );

        // Now assign each of newStarDocRefs by their path, creating a new
        // object with an empty `folder` slot if said path wasn't seen in
        // this.documents
        for (const star of newStarDocRefs) {
            const existing = diff.get(star.path);
            if (existing) {
                existing.star = star;
            } else {
                diff.set(star.path, {
                    folder: null,
                    star,
                });
            }
        }

        const newDocPromises: Array<
            DocumentFolder<any, any> | Promise<DocumentFolder<any, any> | null>
        > = [];
        const retired: DocumentFolder<any, any>[] = [];

        for (const { folder, star } of diff.values()) {
            // diff inspection. This is where we tell whether a folder is
            // going away, already existing, or is being added, thus needs
            // to be created

            if (folder && star && keepExisting) {
                // existing document
                newDocPromises.push(folder);
                continue;
            }
            if (folder) {
                // removed document, or one being replaced by a fresh load
                retired.push(folder);
            }
            if (star) {
                // newly added (or refreshed) document; load
                newDocPromises.push(this.loadStub(star));
            }
        }

        const newDocs = await Promise.all(newDocPromises);
        this.documents = newDocs.filter((doc) => !!doc);

        // Disposed only now that they're out of `documents`, so the
        // dashboard never renders a reset (blanked) folder while the
        // additions load.
        for (const folder of retired) folder.reset();
    }

    private async loadStub(star: StarredDocument) {
        const stub = this.createDoc(star.type, star.path);
        try {
            await stub.load();
        } catch (loadError) {
            console.log(
                '[StarredDocuments] Error loading document:',
                star.path,
                loadError
            );
            stub.reset();
            return null;
        }
        // load() doesn't throw for a document that is missing or can't be
        // read - loadData() catches both and leaves `exists` false - so
        // without this a deleted starred document would list as a blank
        // stub. It stays starred, and is retried on the next update.
        if (!stub.exists) {
            stub.reset();
            return null;
        }
        return stub;
    }

    private createDoc(type: DocumentType, path: string) {
        const docRef = doc(getFirestore(), path);
        const starredDoc = createDocumentFolderForType(type, this, docRef);
        starredDoc.stub = true;
        starredDoc.updatesEnabled = false;
        return starredDoc;
    }

    public async add(doc: DocumentFolder<any, any>) {
        if (this.get(doc)) return;

        const { id, path, type } = doc;
        this.parent.data.starredDocuments.push({ path, id, type });
        await this.parent.save();
    }

    public async remove(doc: DocumentFolder<any, any>) {
        const parentData = this.parent.data;
        const oldLen = parentData.starredDocuments.length;
        parentData.starredDocuments = parentData.starredDocuments.filter(
            (star) => star.path !== doc.path
        );
        if (parentData.starredDocuments.length !== oldLen) {
            await this.parent.save();
        }
    }

    public get(doc: DocumentFolder<any, any>) {
        return this.parent.data.starredDocuments.find(
            (star) => star.path === doc.path
        );
    }
}

export default StarredDocuments;
